import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { formatCurrency } from 'support/helpers/currency/currency';

import { SummarySubItem } from './types';

export type TaxRateAmount = { rate: number; amount: number };
export type TaxBreakdownValueFormat = 'signed' | 'plain';

export const getTaxBreakdown = (items: unknown): Array<TaxRateAmount> => {
  if (!Array.isArray(items)) {
    return [];
  }

  const totalsByRate = new Map<number, number>();

  items.forEach((item: unknown) => {
    if (
      typeof item !== 'object' ||
      item === null ||
      !('tax_percentage' in item) ||
      typeof item.tax_percentage !== 'number' ||
      !Number.isFinite(item.tax_percentage) ||
      !('tax_amount' in item) ||
      typeof item.tax_amount !== 'number' ||
      !Number.isFinite(item.tax_amount)
    ) {
      return;
    }

    totalsByRate.set(item.tax_percentage, (totalsByRate.get(item.tax_percentage) ?? 0) + item.tax_amount);
  });

  return Array.from(totalsByRate, ([rate, amount]) => ({ rate, amount })).sort((a, b) => b.rate - a.rate);
};

export const formatTaxRate = (rate: number) =>
  new Intl.NumberFormat(navigator.language, { maximumFractionDigits: 2 }).format(rate);

export const useTaxBreakdownRows = (
  currency: string | null | undefined,
  valueFormat: TaxBreakdownValueFormat = 'signed',
): Array<SummarySubItem> => {
  const { t } = useTranslation();
  const items = useWatch({ name: 'tax.items' });

  return useMemo(
    () =>
      getTaxBreakdown(items).map(({ rate, amount }) => ({
        label: t('webedi:documentSummary.taxRate.label', { rate: formatTaxRate(rate) }),
        value:
          valueFormat === 'plain' ? (
            formatCurrency(amount, currency)
          ) : (
            <span className="tabular-nums">+ {formatCurrency(amount, currency)}</span>
          ),
      })),
    [currency, items, t, valueFormat],
  );
};
